import apiClient from './client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface MFAStatus {
  totpEnabled: boolean;
  totpVerifiedAt: string | null;
  hasBackupCodes: boolean;
  backupCodesRemaining: number;
  hasPasskeys: boolean;
  passkeyCount: number;
}

export interface TOTPSetupResponse {
  success: boolean;
  secret: string;
  qrCode: string;
  issuer: string;
  account: string;
}

export interface TOTPVerifySetupResponse {
  success: boolean;
  message: string;
  backupCodes: string[];
}

export interface MFAChallengeResponse {
  success: boolean;
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    displayName: string;
    tier: string;
  };
}

export interface PasskeyInfo {
  credentialID: string;
  deviceName: string;
  registeredAt: string;
  lastUsedAt: string | null;
}

// ─── TOTP API ────────────────────────────────────────────────────────────────

export async function getMFAStatus(): Promise<MFAStatus> {
  const res = await apiClient.get<MFAStatus>('/auth/mfa/status');
  return res.data;
}

export async function setupTOTP(): Promise<TOTPSetupResponse> {
  const res = await apiClient.post<TOTPSetupResponse>('/auth/mfa/setup');
  return res.data;
}

export async function verifyTOTPSetup(totpCode: string): Promise<TOTPVerifySetupResponse> {
  const res = await apiClient.post<TOTPVerifySetupResponse>('/auth/mfa/verify-setup', { totpCode });
  return res.data;
}

export async function disableTOTP(totpCode: string): Promise<{ success: boolean; message: string }> {
  const res = await apiClient.post('/auth/mfa/disable', { totpCode });
  return res.data;
}

export async function verifyMFAChallenge(
  mfaToken: string,
  totpCode: string
): Promise<MFAChallengeResponse> {
  const res = await apiClient.post<MFAChallengeResponse>('/auth/mfa/challenge', {
    mfaToken,
    totpCode,
  });
  return res.data;
}

// ─── Passkey API ─────────────────────────────────────────────────────────────

export async function listPasskeys(): Promise<PasskeyInfo[]> {
  const res = await apiClient.get<{ passkeys: PasskeyInfo[] }>('/auth/passkeys');
  return res.data.passkeys;
}

export async function getPasskeyRegistrationOptions(): Promise<PublicKeyCredentialCreationOptionsJSON> {
  const res = await apiClient.post('/auth/passkey/register-options');
  return res.data;
}

export async function registerPasskey(
  credential: RegistrationResponseJSON,
  deviceName?: string
): Promise<{ success: boolean; passkey: PasskeyInfo }> {
  const res = await apiClient.post('/auth/passkey/register', { credential, deviceName });
  return res.data;
}

export async function getPasskeyAuthenticationOptions(
  email?: string
): Promise<PublicKeyCredentialRequestOptionsJSON & { challengeKey: string }> {
  const res = await apiClient.post('/auth/passkey/authenticate-options', { email });
  return res.data;
}

export async function authenticateWithPasskey(
  credential: AuthenticationResponseJSON,
  challengeKey: string
): Promise<MFAChallengeResponse> {
  const res = await apiClient.post<MFAChallengeResponse>('/auth/passkey/authenticate', {
    credential,
    challengeKey,
  });
  return res.data;
}

export async function deletePasskey(credentialID: string): Promise<{ success: boolean }> {
  const res = await apiClient.delete(`/auth/passkey/${encodeURIComponent(credentialID)}`);
  return res.data;
}

// ─── Type shims for WebAuthn (when @simplewebauthn/browser not installed) ────

export type PublicKeyCredentialCreationOptionsJSON = Record<string, unknown>;
export type PublicKeyCredentialRequestOptionsJSON = Record<string, unknown>;
export type RegistrationResponseJSON = Record<string, unknown>;
export type AuthenticationResponseJSON = Record<string, unknown>;
